import useHideMoreView from "@repo/components/src/hooks/useHideMoreView";
import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonIcon, ButtonText } from "@repo/components/src/ui/button";
import {
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
} from "@repo/components/src/ui/checkbox";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import { VStack } from "@repo/components/src/ui/vstack";
import {
  AlertCircleIcon,
  CheckIcon,
  ChevronDown,
  ChevronUp,
} from "lucide-react-native";
import { ComponentProps, useEffect } from "react";
import {
  Controller,
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import Animated from "react-native-reanimated";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputSelectProps } from "../types";

export default function FormInputCheckbox<TFieldValues extends FieldValues>(
  props: Omit<
    FormInputSelectProps<TFieldValues, string[] | number[]>,
    "placeholder"
  > & {
    collapsedVisibleOptions?: number;
    checkboxProps?: Omit<ComponentProps<typeof Checkbox>, "value" | "children">;
    vstackSpacing?: ComponentProps<typeof VStack>["space"];
    getValues?: UseFormGetValues<TFieldValues>;
    setValue?: UseFormSetValue<TFieldValues>;
  },
) {
  const {
    control,
    name,
    label,
    required,
    disabled,
    className,
    size,
    options,
    labelSize,
    checkboxProps,
    collapsedVisibleOptions = 5,
    vstackSpacing = "md",
    getValues,
    setValue,
  } = props;
  const {
    expanded,
    toggleExpanded,
    animatedStyle,
    layoutChange,
    visibleOptions,
    hasMore,
  } = useHideMoreView({ options, collapsedVisibleOptions });
  useEffect(() => {
    if (!getValues || !setValue) return;
    const values = getValues(name);
    const filteredValues = values.filter((v: string) =>
      options.some((o) => o.value === v),
    );
    setValue(name, filteredValues);
  }, [options, getValues, setValue, name]);
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
        <FormControl
          isInvalid={!!error}
          isRequired={required}
          isDisabled={disabled}
          className={className}
          size={size}
          ref={focusScrollRef(ref)}
        >
          {!!label && (
            <FormControlLabel>
              <FormControlLabelText size={labelSize || size}>
                {label}
              </FormControlLabelText>
            </FormControlLabel>
          )}
          <Box className="mt-2">
            <Animated.View style={animatedStyle}>
              <VStack space={vstackSpacing} onLayout={layoutChange}>
                {visibleOptions.map((option) => (
                  <Checkbox
                    size={size}
                    value={option.value as string}
                    key={option.value}
                    onChange={(isChecked) => {
                      const filteredValues = value.filter(
                        (v: string) => v !== option.value,
                      );
                      if (isChecked) {
                        onChange([option.value, ...filteredValues]);
                      } else {
                        onChange(filteredValues);
                      }
                    }}
                    isChecked={value.includes(option.value)}
                    {...checkboxProps}
                  >
                    <CheckboxIndicator>
                      <CheckboxIcon as={CheckIcon} />
                    </CheckboxIndicator>
                    <CheckboxLabel>{option.label}</CheckboxLabel>
                    {option.node}
                  </Checkbox>
                ))}
              </VStack>
            </Animated.View>
            {hasMore && (
              <Box className="flex flex-row mt-2">
                <Button variant="link" size={size} onPress={toggleExpanded}>
                  <ButtonText>
                    {expanded ? "Mostrar menos" : "Mostrar más"}
                  </ButtonText>
                  <ButtonIcon
                    className="ml-2"
                    as={expanded ? ChevronUp : ChevronDown}
                  />
                </Button>
              </Box>
            )}
          </Box>
          <FormControlError>
            <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
            <FormControlErrorText>{error?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
      )}
    />
  );
}
